import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Radio, Crosshair, Cpu, Eye, TrendingUp, ShieldAlert, Wrench,
  Search, Sun, Moon, Monitor, ChevronDown,
  User, Settings, CreditCard, Users, Globe, LogOut, Clock,
} from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import { useAuth } from '../shared/context/AuthContext';
import { useActiveProduct } from '../shared/hooks/useActiveProduct';
import { PRODUCTS, PRODUCT_ORDER } from '../shared/config/productConfig';

/** Map icon names from productConfig to actual Lucide components */
const PRODUCT_ICONS = { Shield, Radio, Crosshair, Cpu, Eye, TrendingUp, ShieldAlert, Wrench };

const TopNav = () => {
  const navigate = useNavigate();
  const activeProduct = useActiveProduct();
  const [theme, setTheme] = useState('light');
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const auth = useAuth();
  const profileRef = useRef(null);
  const themeRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
      if (themeRef.current && !themeRef.current.contains(e.target)) setShowThemeMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    setShowThemeMenu(false);
    if (newTheme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  };

  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const initials = (auth?.user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const isAdmin = auth?.user?.role === 'admin';

  const isStaging = process.env.REACT_APP_ENV === 'staging';

  return (
    <>
      {isStaging && (
        <div className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-2 bg-amber-400 text-amber-900 text-xs font-bold py-0.5 tracking-wider uppercase">
          ⚠ STAGING ENVIRONMENT — staging.threatlens360.com — changes here do not affect production
        </div>
      )}
    <header className={`fixed left-0 right-0 h-topnav bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800 z-50 shadow-sm ${isStaging ? 'top-6' : 'top-0'}`}>
      <div className="h-full flex items-center">

        {/* Left: Logo (aligned with sidebar width) */}
        <div
          className="w-sidebar flex items-center gap-3 px-5 h-full bg-navy-900 cursor-pointer flex-shrink-0"
          onClick={() => navigate('/dashboard')}
        >
          <div className="w-8 h-8 rounded-lg gradient-teal flex items-center justify-center">
            <Shield className="w-4.5 h-4.5 text-white" />
          </div>
          <span className="text-base font-bold text-white tracking-tight">ThreatLens360</span>
        </div>

        {/* Center: Product Tabs */}
        <nav className="flex items-center h-full flex-1 px-2 overflow-x-auto">
          {PRODUCT_ORDER.map((productId) => {
            const product = PRODUCTS[productId];
            const Icon = PRODUCT_ICONS[product.icon];
            const isActive = activeProduct.id === productId;

            return (
              <button
                key={productId}
                onClick={() => navigate(product.defaultRoute)}
                className={`relative flex items-center gap-2 px-8 h-full text-base font-semibold transition-colors whitespace-nowrap ${
                  isActive
                    ? 'text-gray-900 dark:text-white'
                    : product.comingSoon
                    ? 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" style={isActive ? { color: product.color } : undefined} />}
                <span>{product.name}</span>
                {product.comingSoon && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-navy-800 text-gray-400 dark:text-gray-500 font-normal">Soon</span>
                )}
                {/* Active indicator bar */}
                {isActive && (
                  <div
                    className="absolute bottom-0 left-4 right-4 h-[3px] rounded-t-full"
                    style={{ backgroundColor: product.color }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Right: Search, Theme, Notifications, Profile */}
        <div className="flex items-center gap-2 px-4 flex-shrink-0">
          {/* Search */}
          <div className="relative hidden lg:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-9 pr-3 py-1.5 w-44 bg-gray-100 dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Theme Toggle */}
          <div className="relative" ref={themeRef}>
            <button
              onClick={() => setShowThemeMenu(!showThemeMenu)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-800 transition-colors"
            >
              <ThemeIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            {showThemeMenu && (
              <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-navy-800 rounded-lg shadow-lg border border-gray-200 dark:border-navy-700 py-1 z-50">
                {[['light', Sun, 'Light'], ['dark', Moon, 'Dark'], ['system', Monitor, 'System']].map(([key, Icon, label]) => (
                  <button
                    key={key}
                    onClick={() => toggleTheme(key)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Icon className="w-4 h-4" />{label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notifications */}
          <NotificationCenter />

          {/* User Profile */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-1.5 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-800 transition-colors"
              data-testid="user-profile-btn"
            >
              <div className="w-7 h-7 rounded-full bg-teal-500 flex items-center justify-center text-white font-semibold text-xs">
                {initials}
              </div>
              <ChevronDown className="w-3 h-3 text-gray-500" />
            </button>

            {showProfile && (
              <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-navy-800 rounded-xl shadow-2xl border border-gray-200 dark:border-navy-700 z-50 overflow-hidden" data-testid="user-profile-dropdown">
                <div className="px-4 py-3 bg-gray-50 dark:bg-navy-900 border-b border-gray-100 dark:border-navy-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{auth?.user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{auth?.user?.email || ''}</p>
                  <span className="inline-block mt-1 text-xs uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                    {auth?.user?.role || 'viewer'}
                  </span>
                </div>
                <div className="py-1">
                  <ProfileLink icon={User} label="Profile" to="/settings/profile" onClick={() => setShowProfile(false)} />
                  <ProfileLink icon={Settings} label="Account Settings" to="/settings/account" onClick={() => setShowProfile(false)} />
                  {isAdmin && <ProfileLink icon={CreditCard} label="Payment Details" to="/settings/billing" onClick={() => setShowProfile(false)} />}
                  {isAdmin && <ProfileLink icon={Users} label="Team" to="/settings/team" onClick={() => setShowProfile(false)} />}
                  {isAdmin && <ProfileLink icon={Globe} label="Domains" to="/settings/domains" onClick={() => setShowProfile(false)} />}
                </div>
                <div className="border-t border-gray-100 dark:border-navy-700 py-1">
                  <button
                    onClick={() => { setShowProfile(false); auth?.logout?.(); navigate('/login'); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    data-testid="sign-out-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

function ProfileLink({ icon: Icon, label, to, onClick }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors">
      <Icon className="w-4 h-4 text-gray-400" />
      {label}
    </Link>
  );
}

export default TopNav;
